import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Heart, Map, Users, Compass, Star } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const CompanyPresentation = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const images = [
    "https://iili.io/3KGkpHl.jpg",
    "https://iili.io/3KGvWfs.jpg",
    "https://iili.io/3KG8BBn.jpg"
  ];

  return (
    <section id="presentation" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full -translate-y-1/2 translate-x-1/2 opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-sand-50 rounded-full translate-y-1/2 -translate-x-1/2 opacity-30"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeInUp} ref={ref} className="text-center mb-16">
          <span className="text-blue-900 font-medium tracking-wider uppercase text-sm">Rendez-vous Sans Frontières</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mt-4 mb-6">Votre passerelle vers l'essence du Maroc</h2>
          <div className="w-24 h-1 bg-blue-900 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Nous créons des voyages qui transcendent le tourisme classique pour vous immerger pleinement dans la richesse culturelle marocaine
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeInUp} className="relative">
            <Swiper
              modules={[EffectFade, Autoplay, Pagination]}
              effect="fade"
              autoplay={{ delay: 5000 }}
              pagination={{ clickable: true }}
              loop={true}
              className="rounded-2xl shadow-2xl overflow-hidden aspect-[4/3]"
            >
              {images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div className="relative w-full h-full">
                    <img src={image} alt="Voyages au Maroc" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>

          <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={staggerContainer} className="space-y-6">
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">Notre Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous pensons qu'un véritable voyage dépasse les simples visites touristiques. C'est une rencontre profonde avec la culture locale, un échange enrichissant, et une occasion unique de se reconnecter à soi-même.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="grid sm:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-xl transform hover:scale-105 transition-all duration-300">
                <Users className="w-8 h-8 text-blue-900 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Petits Groupes</h3>
                <p className="text-gray-600">
                  4 à 8 personnes maximum pour une expérience intime et personnalisée
                </p>
              </div>
              <div className="bg-sand-50 p-6 rounded-xl transform hover:scale-105 transition-all duration-300">
                <Star className="w-8 h-8 text-blue-900 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Expériences Uniques</h3>
                <p className="text-gray-600">
                  Des moments exclusifs loin des sentiers touristiques traditionnels
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
          <Feature
            icon={Compass}
            title="Circuits Sur Mesure"
            text="Des itinéraires personnalisés selon vos envies et votre rythme"
          />
          <Feature
            icon={Heart}
            title="Immersion Culturelle"
            text="Vivez pleinement les traditions et la vie locale"
          />
          <Feature
            icon={Map}
            title="Destinations Uniques"
            text="Des lieux préservés et des expériences exclusives"
          />
        </motion.div>
      </div>
    </section>
  );
};

const Feature = ({ icon: Icon, title, text }) => (
  <motion.div variants={fadeInUp} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:-translate-y-1 transition-transform">
    <Icon className="w-10 h-10 text-blue-900 mb-4" />
    <h3 className="text-xl font-bold text-gray-900 mb-4">{title}</h3>
    <p className="text-gray-600">{text}</p>
  </motion.div>
);

export default CompanyPresentation;